import { gql } from "@apollo/client";
import { ActorRefFrom } from "xstate";
import { createModel } from "xstate/lib/model";

import { graphQLClient } from "../../api/graphql";
import { openReportURL } from "../generateReportConfig.machine";

export interface SupplierReengagementReportActor
  extends ActorRefFrom<typeof supplierReengagementReportMachine> {}

export const supplierReengagementReportModel = createModel(
  {
    dateRange: {
      startDate: new Date(),
      endDate: new Date(),
    },
    reportURL: undefined as string | undefined,
    error: undefined as string | undefined,
  },
  {
    events: {
      provideReportDates: (value: { startDate: Date; endDate: Date }) => ({ data: value }),
      goBack: () => ({}),
      generateReport: () => ({}),
      reportGenerationSuccess: (value: string) => ({ data: { reportURL: value } }),
      reportGenerationFailure: (value: string) => ({ data: { error: value } }),
      restart: () => ({}),
    },
  }
);

export const supplierReengagementReportMachine = supplierReengagementReportModel.createMachine(
  {
    context: supplierReengagementReportModel.initialContext,
    initial: "configuringDateRange",
    states: {
      configuringDateRange: {
        id: "configuringDateRange",
        on: {
          provideReportDates: {
            actions: supplierReengagementReportModel.assign({
              dateRange: (_, ev) => {
                return ev.data;
              },
            }),
            target: "showingReportConfigSummary",
          },
        },
      },
      showingReportConfigSummary: {
        initial: "idle",
        states: {
          idle: {
            on: {
              generateReport: {
                target: "generatingReport",
              },
              goBack: {
                target: "#configuringDateRange",
              },
            },
          },
          generatingReport: {
            invoke: {
              id: "generatingReport",
              src: "generateReport",
              onError: {
                target: "#failure",
              },
            },
            on: {
              reportGenerationSuccess: {
                target: "#reportGenerated",
                actions: supplierReengagementReportModel.assign({
                  reportURL: (_, ev) => ev.data.reportURL,
                }),
              },
              reportGenerationFailure: {
                target: "#failure",
                actions: supplierReengagementReportModel.assign({
                  error: (_, ev) => ev.data.error,
                }),
              },
            },
          },
        },
      },
      reportGenerated: {
        id: "reportGenerated",
        entry: "openReportURL",
      },
      failure: {
        id: "failure",
        on: {
          restart: {
            target: "configuringDateRange",
            actions: supplierReengagementReportModel.assign(() => {
              return supplierReengagementReportModel.initialContext;
            }),
          },
        },
      },
    },
  },
  {
    actions: {
      openReportURL: async (ctx) => {
        if (!ctx.reportURL) {
          throw new Error("Response does not contain a url");
        }

        openReportURL(ctx.reportURL);
      },
    },
    services: {
      generateReport: (ctx, ev) => (cb) => {
        graphQLClient
          .query({
            query: GENERATE_SUPPLIER_REENGAGEMENT_REPORT,
            variables: {
              input: {
                startDate: ctx.dateRange.startDate,
                endDate: ctx.dateRange.endDate,
              },
            },
          })
          .then((res) => {
            const reportUrl = res.data.supplierReengagementReport.url;

            cb(supplierReengagementReportModel.events.reportGenerationSuccess(reportUrl));
          })
          .catch((e) => {
            cb(supplierReengagementReportModel.events.reportGenerationFailure(e.message));
          });
      },
    },
  }
);

const GENERATE_SUPPLIER_REENGAGEMENT_REPORT = gql`
  query GetSupplierReengagementReportInfo($input: SupplierReengagementReportInput!) {
    supplierReengagementReport(input: $input) {
      url
    }
  }
`;
